import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamily } from '../../contexts/FamilyContext';
import { toast } from 'react-toastify';
import { ChevronDown } from 'lucide-react';

const FAMILY_COLORS = ['#556B2F', '#8B4513', '#2E4A62', '#6B8E23', '#7B1FA2', '#1565C0'];
const getFamilyColor = (name) =>
  FAMILY_COLORS[
    (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % FAMILY_COLORS.length
  ];

/**
 * Family Switcher dropdown component.
 * Shows for ALL logged-in users (single-family see 1 card + "Create or Join").
 */
function FamilySwitcher() {
  const { families, activeFamily, activeFamilyId, primaryFamilyId, switchFamily } = useFamily();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Show for all logged-in users (no isMultiFamily gate)
  if (!families || families.length === 0) return null;

  const handleSwitch = async (familyId) => {
    if (familyId === activeFamilyId) {
      setIsOpen(false);
      return;
    }

    await switchFamily(familyId);
    const selectedFamily = families.find(f => f.id === familyId);
    toast.success(`Switched to ${selectedFamily?.name || 'family'}`, {
      autoClose: 2000,
    });
    setIsOpen(false);

    // Reload the page so all components re-fetch data for the new family
    window.location.reload();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger button — high-prominence warm pill */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          background: '#FFFBF5', border: 'none', borderRadius: 999,
          padding: '5px 8px 5px 6px',
          boxShadow: '0 3px 10px rgba(40,46,28,.22)',
          cursor: 'pointer', transition: 'transform .12s',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
        aria-label="Switch family"
        aria-expanded={isOpen}
      >
        {/* Family crest avatar */}
        <div style={{
          width: 32, height: 32, borderRadius: '50%', background: '#556B2F',
          color: '#fff', fontWeight: 700, fontSize: 14,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {(activeFamily?.name || 'F').charAt(0)}
        </div>
        {/* Two-line label */}
        <div style={{ lineHeight: 1.08, textAlign: 'left' }}>
          <div style={{
            fontSize: 9.5, fontWeight: 700, letterSpacing: '.8px',
            textTransform: 'uppercase', color: '#9AA088',
          }}>
            VIEWING
          </div>
          <div style={{
            fontSize: 14, fontWeight: 700, color: '#3E4F22',
            maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {activeFamily?.name ? `${activeFamily.name} Family` : 'Select Family'}
          </div>
        </div>
        {/* Chevron chip */}
        <div style={{
          width: 24, height: 24, borderRadius: '50%', background: '#EDE8CD',
          color: '#556B2F', display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <ChevronDown size={15} style={{ transition: 'transform .2s', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-[280px] bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 pt-4 pb-2">
            <span className="text-[12px] font-semibold text-gray-400 uppercase tracking-wider">
              YOUR FAMILIES
            </span>
          </div>

          {/* Family cards */}
          <div className="px-2 pb-2 space-y-1">
            {families.map((family) => {
              const isActive = family.id === activeFamilyId;
              const isPrimary = family.id === primaryFamilyId;
              const color = getFamilyColor(family.name);

              return (
                <button
                  key={family.id}
                  onClick={() => handleSwitch(family.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left ${
                    isActive
                      ? 'bg-[#F0F4E8]'
                      : 'bg-white hover:bg-gray-50 border border-gray-100'
                  }`}
                >
                  {/* Color icon square */}
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: color }}
                  >
                    <span className="text-white text-base">🏠</span>
                  </div>

                  {/* Family info */}
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-bold text-gray-900 truncate">
                      {family.name}
                    </div>
                    <div className="text-[12px] text-gray-500">
                      {family.member_count != null
                        ? `${family.member_count} ${family.member_count === 1 ? 'member' : 'members'}`
                        : 'Family'}
                      {isPrimary && (
                        <span className="text-gray-400"> · Primary family</span>
                      )}
                    </div>
                  </div>

                  {/* Active checkmark */}
                  {isActive && (
                    <div className="w-5 h-5 rounded-full bg-[#556B2F] flex items-center justify-center flex-shrink-0">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                        <path d="M5 13l4 4L19 7" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100 mx-3" />

          {/* Create or Join */}
          <div className="px-2 py-2">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/manage-families');
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-full border-2 border-dashed border-[#6B8E23] flex items-center justify-center flex-shrink-0">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B8E23" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </div>
              <span className="text-[14px] font-medium text-[#6B8E23]">
                Create or Join a Family
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FamilySwitcher;
